import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import priceSimulationActions from '../../Actions/priceSimulationActions';
import type { FilterOption, RootState } from '../../Components/PriceSimulation/PriceSimulation.types';
import { useAppDispatch } from '../../Store/types';

const usePackageContexts = (allPackages: FilterOption[], selectedPackages: FilterOption[]) => {
    const dispatch = useAppDispatch();
    const packageDetailsMap = useSelector((state: RootState) => state.PriceSimulationReducer.packageDetailsMap);

    const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(() => new Set());
    const inFlightPackageIdsRef = useRef(new Set<string>());

    const unloadedPackages = useMemo(() => allPackages.filter((pkg) => !(pkg.id in packageDetailsMap)), [allPackages, packageDetailsMap]);
    const unloadedSelectedPackages = useMemo(() => {
        const selectedIds = new Set(selectedPackages.map((pkg) => String(pkg.id)));
        return unloadedPackages.filter((pkg) => selectedIds.has(String(pkg.id)));
    }, [unloadedPackages, selectedPackages]);
    const failedSelectedPackages = useMemo(() => unloadedSelectedPackages.filter((pkg) => failedIds.has(String(pkg.id))), [unloadedSelectedPackages, failedIds]);
    const failedUnselectedCount = useMemo(() => {
        const selectedIds = new Set(selectedPackages.map((pkg) => String(pkg.id)));
        return unloadedPackages.filter((pkg) => failedIds.has(String(pkg.id)) && !selectedIds.has(String(pkg.id))).length;
    }, [unloadedPackages, selectedPackages, failedIds]);

    // Every package loads in one batched request so SKU links work across packages. In-flight ids are skipped so the
    // re-run after each store update does not request them again; a failed package is not retried until Retry.
    useEffect(() => {
        const idsToFetch = unloadedPackages.map((pkg) => String(pkg.id)).filter((id) => !inFlightPackageIdsRef.current.has(id) && !failedIds.has(id));
        if (idsToFetch.length === 0) return;

        idsToFetch.forEach((id) => inFlightPackageIdsRef.current.add(id));
        void dispatch(priceSimulationActions.fetchPriceSimulationPackageContexts(idsToFetch)).then((failed) => {
            idsToFetch.forEach((id) => inFlightPackageIdsRef.current.delete(id));
            if (failed.length > 0) setFailedIds((prev) => new Set([...prev, ...failed]));
        });
    }, [unloadedPackages, failedIds, dispatch]);

    const retry = useCallback(() => setFailedIds(new Set()), []);

    return {
        isLoadingPackages: failedSelectedPackages.length < unloadedSelectedPackages.length,
        unloadedPackageNames: failedSelectedPackages.map((pkg) => pkg.name ?? String(pkg.id)),
        failedUnselectedCount,
        allPackagesLoaded: allPackages.length > 0 && unloadedPackages.length === 0,
        retry,
    };
};

export default usePackageContexts;
